import type { ConnectionDescriptor } from '@nx/constants';
import type { ParseableError } from '@nx/errors';

export type Connection = {
  /**
   * User-defined connection display name
   * Defaults to the connection ID - host and port
   */
  name: string;

  /**
   * Database credentials to authenticate with
   */
  credentials: ConnectionDescriptor;

  /**
   * Connection error as returned by driver
   */
  error?: ParseableError;

  /**
   * Last connection time as Unix timestamp
   */
  lastUsedAt?: number;

  /**
   * Connection URL
   */
  url: string;

  /**
   * Discovery URLs
   */
  discoveryUrls?: string[];

  /**
   * Mark connection for removal
   * Used to enabled undo
   */
  deleted?: boolean;

  /**
   * Mark connection as transient
   * Used to mark connection as one-off, to be removed after use
   */
  isTransient?: boolean;

  /**
   * Connection details origin
   *
   * NB! This is about connection details, not instance origin, i.e. if user
   * connects to Aura DB manually via “external connections” UI, the source
   * is `user` and not `aura`.
   *
   * - `type`: Specifies the origin of the connection details.
   *   - `user`: Custom connection, details are fully managed by the end user.
   *   - `aura`: In-project connection, details are managed by Console API.
   *   - `desktop`: Desktop connection, details are managed by Desktop/Relate.
   *   - `fleet-management`: Enterprise Fleet Management Plugin, details are managed by the deployment API.
   *
   * - `sourceId`: A unique identifier for the connection source.
   *   - For `user`, this is undefined since we don't have a stable id for the connection. For now you can use the url
   *     as identifier.
   *   - For `aura`, this is the Aura instance id.
   *   - For `desktop`, this is the desktop instance id.
   *   - For `fleet-management`, this is the fleet management deployment id.
   */
  source:
    | {
        type: 'user';
        sourceId?: undefined;
      }
    | {
        type: 'aura';
        sourceId: string;
      }
    | {
        type: 'desktop';
        sourceId: string;
      }
    | {
        type: 'fleet-management';
        sourceId: string;
      };
};

export function isConnectionState(input: unknown): input is Connection {
  if (typeof input !== 'object') {
    return false;
  }

  if (input === null) {
    return false;
  }

  return 'url' in input && 'credentials' in input;
}
